import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAutoSave } from '../../../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../../../composants/AutoSaveIndicator/AutoSaveIndicator';
import TabNav from '../../../../composants/TabNav/TabNav';
import Button from '../../../../composants/Button/Button';
import ValidationPopUp from '../../../../composants/ValidationPopUp/ValidationPopUp';
import CandidatureHeader from './CandidatureHeader';
import SectionCandidat from './SectionCandidat';
import SectionChoixLots from './SectionChoixLots';
import SectionOffreTechnique, { CRITERES_TECHNIQUES } from './SectionOffreTechnique';
import SectionOffreFinanciere from './SectionOffreFinanciere';
import type { Article, PrixLigne, RegionGroup } from './SectionOffreFinanciere';
import SectionSoumission from './SectionSoumission';
import type { ValidationItem } from './SectionSoumission';
import ModalProfilMembre from './ModalProfilMembre';
import BottomActionBar from './BottomActionBar';
import type { ProfileData } from '../../../../services/profileService';
import './DetailCandidature.css';
import { getStatusColor } from '../../../../utils/entityUtils';
import { getLotImage } from '../../../../utils/getLotImage';
import SuiviHeader from '../../../../composants/SuiviHeader/SuiviHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EntrepriseInfo {
  id: number;
  nom: string;
  siret: string;
  representant?: string;
  email?: string;
  telephone?: string;
  progression: number;
}

interface GroupementMember {
  id: number;
  entreprise: EntrepriseInfo;
  entite_profil?: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
  } | null;
  accepted_at: string | null;
  status: 'pending' | 'accepted' | 'refused';
}

const PRIX_LIGNE_VIDE: PrixLigne = { prix_a: '', prix_b: '', prix_c: '' };

interface LotDisponible {
  id: number;
  region: boolean;
  departement?: {
    id: number;
    nom: string;
    id_region?: number;
    region?: { id: number; nom: string };
  };
}

// Helpers pour la logique lots 1xx / 2xx
const lotCodeIsRegional = (code: string): boolean => {
  const n = parseInt(code, 10);
  return n >= 100 && n < 200;
};

/**
 * Construit les groupes de régions depuis les lots_departements d'une candidature.
 * Utilisé pour les lots 1xx : regroupe les lots_départements par région.
 */
const buildRegionGroups = (
  lotsDepartements: Array<{ id: number; departement?: { id: number; nom: string; region?: { id: number; nom: string } } }>,
  selectedIds: number[],
): RegionGroup[] => {
  const map = new Map<number, RegionGroup>();
  lotsDepartements
    .filter((l) => selectedIds.includes(l.id))
    .forEach((l) => {
      const regionId = l.departement?.region?.id ?? 0;
      const regionNom = l.departement?.region?.nom ?? 'Région inconnue';
      if (!map.has(regionId)) map.set(regionId, { regionId, regionNom, lots: [] });
      map.get(regionId)!.lots.push({ id: l.id, departement: l.departement });
    });
  return Array.from(map.values()).sort((a, b) => a.regionNom.localeCompare(b.regionNom));
};

interface CandidatureDetail {
  id: number;
  status: 'brouillon' | 'soumise';
  entite_id: number;
  is_mandataire: boolean;
  lots_disponibles?: LotDisponible[];
  lots_geo_ids: number[];
  accord_cadre: {
    nom: string;
    acheteur?: string;
    poids_offre_technique?: number;
    poids_offre_financiere?: number;
    reference_avis?: string;
    expire_at?: string;
  };
  lot_technique: {
    id: number;
    code: string;
    nom: string;
    cpv?: string;
    articles?: Article[];
  };
  lots_departements: Array<{
    id: number;
    id_departement?: number;
    departement?: {
      id: number;
      nom: string;
      region?: { id: number; nom: string };
    };
  }>;
  groupement_members?: GroupementMember[];
  entite: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
    representant?: string;
    email?: string;
    telephone?: string;
  };
  offre_technique?: {
    description_technique?: string | null;
    description_operationnel?: string | null;
    description_humain?: string | null;
    description_environnement?: string | null;
  };
  offre_financiere?: {
    prix_identiques: boolean;
    prix?: Array<{
      article_id: number;
      lot_departement_id: number;
      prix_a: number | null;
      prix_b: number | null;
      prix_c: number | null;
    }>;
  };
}

interface CheckProfilsResult {
  all_complete: boolean;
  profils: Array<{
    entreprise_id: number;
    entreprise_nom: string;
    progression: number;
    mandataire: boolean;
  }>;
}

// ── Composant principal ───────────────────────────────────────────────────────

const DetailCandidature: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const authHeaders: HeadersInit = { Authorization: `Bearer ${token}` };

  const [candidature, setCandidature] = useState<CandidatureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(1); // "Candidature" par défaut
  const [showInviterModal, setShowInviterModal] = useState(false);

  // Offre technique
  const [offreTechnique, setOffreTechnique] = useState<Record<string, string>>({});

  // Offre financière
  const [prixIdentiques, setPrixIdentiques] = useState(true);
  const [prixLignes, setPrixLignes] = useState<Record<number, PrixLigne>>({});
  const [prixParLot, setPrixParLot] = useState<Record<number, Record<number, PrixLigne>>>({});
  /** Prix par région pour lots 1xx en mode "prix différents par région" */
  const [prixParRegion, setPrixParRegion] = useState<Record<number, Record<number, PrixLigne>>>({});

  // Lots géo locaux (pour mise à jour sans rechargement API)
  const [lotsGeoIds, setLotsGeoIds] = useState<number[]>([]);

  // Popup confirmation toggle prix (Story 13.5)
  const [showTogglePrixConfirm, setShowTogglePrixConfirm] = useState(false);

  // Soumission (Story 8.9)
  const [checkProfils, setCheckProfils] = useState<CheckProfilsResult | null>(null);
  const [isDeposing, setIsDeposing] = useState(false);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [showConfirmDeposer, setShowConfirmDeposer] = useState(false);
  const [deposerError, setDeposerError] = useState<string | null>(null);

  // Story 8.12 — Modale profil membre groupement
  const [showMemberProfileModal, setShowMemberProfileModal] = useState(false);
  const [memberProfile, setMemberProfile] = useState<ProfileData | null>(null);
  const [memberProfileLoading, setMemberProfileLoading] = useState(false);
  const [memberProfileError, setMemberProfileError] = useState<string | null>(null);

  const TABS_DETAIL = ['Avis et documents', 'Candidature', 'Questions / Réponses'];

  // Seul enrichissement nécessaire : extraire lots_geo_ids depuis lots_departements
  const normalizeApiData = (raw: any): CandidatureDetail => ({
    ...raw,
    lots_geo_ids: (raw.lots_departements ?? []).map((l: any) => l.id),
  });

  /** Charge (ou recharge) check-profils depuis l'API */
  const loadCheckProfils = useCallback(() => {
    if (!id) return;
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}/check-profils`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setCheckProfils(d?.data ?? d))
      .catch(() => { /* silencieux — les items resteront à null */ });
  }, [id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Story 8.12 — Ouvre la modale profil membre et charge les données */
  const openMemberProfileModal = useCallback((entrepriseId: number) => {
    setMemberProfile(null);
    setMemberProfileError(null);
    setMemberProfileLoading(true);
    setShowMemberProfileModal(true);

    fetch(
      `${apiUrl}/publications/ac27/candidatures/${id}/membres/${entrepriseId}/profil`,
      { headers: authHeaders },
    )
      .then((r) => {
        if (r.status === 403) throw new Error('Accès refusé : vous n\'êtes pas mandataire de cette candidature.');
        if (r.status === 404) throw new Error('Ce membre n\'est pas un cotraitant accepté de cette candidature.');
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d) => setMemberProfile(d?.data ?? d))
      .catch((err: Error) => setMemberProfileError(err.message || 'Impossible de charger le profil.'))
      .finally(() => setMemberProfileLoading(false));
  }, [id, apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCandidature = useCallback(() => {
    if (!id) return;
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        const raw = d?.data ?? d;
        const data = normalizeApiData(raw);
        setCandidature(data);
        setLotsGeoIds(data.lots_geo_ids ?? []);
        const identiques = data.offre_financiere?.prix_identiques ?? true;
        setPrixIdentiques(identiques);

        const ot = data.offre_technique ?? {};
        setOffreTechnique({
          description_technique: ot.description_technique ?? '',
          description_operationnel: ot.description_operationnel ?? '',
          description_humain: ot.description_humain ?? '',
          description_environnement: ot.description_environnement ?? '',
        });

        // Parser les prix depuis l'API (tableau article × département)
        const prixApi = data.offre_financiere?.prix ?? [];
        const isReg = lotCodeIsRegional(raw.lot_technique?.code ?? '0');

        if (identiques) {
          // Tous les départements ont les mêmes prix → prendre le premier lot rencontré
          const lignes: Record<number, PrixLigne> = {};
          prixApi.forEach((p) => {
            if (!lignes[p.article_id]) {
              lignes[p.article_id] = {
                prix_a: String(p.prix_a ?? ''),
                prix_b: String(p.prix_b ?? ''),
                prix_c: String(p.prix_c ?? ''),
              };
            }
          });
          setPrixLignes(lignes);
        } else if (isReg) {
          // Lot 1xx prix différents → grouper par RÉGION (prendre le premier dept de chaque région)
          const lotsDepts = data.lots_departements ?? [];
          // Map lot_departement_id → region_id
          const lotToRegion: Record<number, number> = {};
          lotsDepts.forEach((l: any) => {
            const regionId = l.departement?.region?.id ?? 0;
            lotToRegion[l.id] = regionId;
          });
          const byRegion: Record<number, Record<number, PrixLigne>> = {};
          prixApi.forEach((p) => {
            const regionId = lotToRegion[p.lot_departement_id] ?? 0;
            if (!byRegion[regionId]) byRegion[regionId] = {};
            // Prendre le premier prix rencontré pour chaque article de la région
            if (!byRegion[regionId][p.article_id]) {
              byRegion[regionId][p.article_id] = {
                prix_a: String(p.prix_a ?? ''),
                prix_b: String(p.prix_b ?? ''),
                prix_c: String(p.prix_c ?? ''),
              };
            }
          });
          setPrixParRegion(byRegion);
        } else {
          // Lot 2xx prix différents → grouper par lot_departement_id
          const byLot: Record<number, Record<number, PrixLigne>> = {};
          prixApi.forEach((p) => {
            if (!byLot[p.lot_departement_id]) byLot[p.lot_departement_id] = {};
            byLot[p.lot_departement_id][p.article_id] = {
              prix_a: String(p.prix_a ?? ''),
              prix_b: String(p.prix_b ?? ''),
              prix_c: String(p.prix_c ?? ''),
            };
          });
          setPrixParLot(byLot);
        }
      })
      .then(() => loadCheckProfils())
      .catch(() => setCandidature(null))
      .finally(() => setLoading(false));
  }, [id, apiUrl, loadCheckProfils]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCandidature();
  }, [loadCandidature]);

  // ── buildOffreFinancierePayload (déplacé avant les hooks auto-save) ────────

  const buildOffreFinancierePayload = useCallback(() => {
    const prix: Array<{
      article_id: number;
      lot_departement_id: number;
      prix_a: number;
      prix_b: number;
      prix_c: number;
    }> = [];

    const isReg = lotCodeIsRegional(candidature?.lot_technique?.code ?? '0');
    const selectedLotsDepts = (candidature?.lots_departements ?? []).filter((l) =>
      lotsGeoIds.includes(l.id)
    );

    if (prixIdentiques) {
      lotsGeoIds.forEach((lotId) => {
        Object.entries(prixLignes).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: lotId,
            prix_a: parseFloat(p.prix_a) || 0,
            prix_b: parseFloat(p.prix_b) || 0,
            prix_c: parseFloat(p.prix_c) || 0,
          });
        });
      });
    } else if (isReg) {
      selectedLotsDepts.forEach((l) => {
        const regionId = l.departement?.region?.id ?? 0;
        const regionPrix = prixParRegion[regionId] ?? {};
        Object.entries(regionPrix).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: l.id,
            prix_a: parseFloat(p.prix_a) || 0,
            prix_b: parseFloat(p.prix_b) || 0,
            prix_c: parseFloat(p.prix_c) || 0,
          });
        });
      });
    } else {
      lotsGeoIds.forEach((lotId) => {
        const articlesLot = prixParLot[lotId] ?? {};
        Object.entries(articlesLot).forEach(([articleId, p]) => {
          prix.push({
            article_id: Number(articleId),
            lot_departement_id: lotId,
            prix_a: parseFloat(p.prix_a) || 0,
            prix_b: parseFloat(p.prix_b) || 0,
            prix_c: parseFloat(p.prix_c) || 0,
          });
        });
      });
    }

    return { prix_identiques: prixIdentiques, prix };
  }, [candidature, lotsGeoIds, prixIdentiques, prixLignes, prixParLot, prixParRegion]);

  // ── Auto-Save hooks (Story 13.1/13.2/13.3/13.4) ──────────────────────────

  // Auto-save lots (Story 13.2)
  const lotsAutoSave = useAutoSave({
    data: lotsGeoIds,
    saveFn: async (ids) => {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/lots-departements`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lot_departement_ids: ids, prix_identiques: prixIdentiques }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
      // Re-fetch complet pour récupérer les prix auto-créés par le backend
      await loadCandidature();
    },
    delay: 2000,
    enabled: candidature?.status !== 'soumise',
  });

  // Auto-save offre technique (Story 13.3)
  const otAutoSave = useAutoSave({
    data: offreTechnique,
    saveFn: async (data) => {
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/offre-technique`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
    },
    delay: 2000,
    enabled: candidature?.status !== 'soumise',
  });

  // Auto-save offre financière (Story 13.4)
  const isRegionalForAutoSave = lotCodeIsRegional(candidature?.lot_technique?.code ?? '0');

  const offreFinanciereData = useMemo(() => ({
    prixIdentiques,
    prixLignes: prixIdentiques ? prixLignes : undefined,
    prixParLot: !prixIdentiques && !isRegionalForAutoSave ? prixParLot : undefined,
    prixParRegion: !prixIdentiques && isRegionalForAutoSave ? prixParRegion : undefined,
  }), [prixIdentiques, prixLignes, prixParLot, prixParRegion, isRegionalForAutoSave]);

  const ofAutoSave = useAutoSave({
    data: offreFinanciereData,
    saveFn: async () => {
      if (!candidature) return;
      const payload = buildOffreFinancierePayload();
      const res = await fetch(`${apiUrl}/publications/ac27/candidatures/${id}/offre-financiere`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const error: any = new Error(err?.message ?? `Erreur ${res.status}`);
        error.status = res.status;
        error.errors = err?.errors;
        throw error;
      }
    },
    delay: 2000,
    enabled: candidature?.status !== 'soumise',
  });

  // ── Protection navigation (Story 13.7) ──────────────────────────────────────

  const hasPendingChanges = lotsAutoSave.status === 'pending' || otAutoSave.status === 'pending' || ofAutoSave.status === 'pending';

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingChanges) {
        e.preventDefault();
        lotsAutoSave.flush();
        otAutoSave.flush();
        ofAutoSave.flush();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && hasPendingChanges) {
        lotsAutoSave.flush();
        otAutoSave.flush();
        ofAutoSave.flush();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasPendingChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handler "Quitter" (Story 13.7) ──────────────────────────────────────────

  const handleQuitter = async () => {
    await lotsAutoSave.flush();
    await Promise.all([otAutoSave.flush(), ofAutoSave.flush()]);
    navigate('/publications?tab=mes-candidatures');
  };

  const getDisabledTabs = useCallback(() => {
    if (!candidature) return [];

    const disabledTabs: number[] = [];

    return disabledTabs;
  }, [candidature]);

  const getInactiveTabs = useCallback(() => {
    if (!candidature) return [];
    const inactiveTabs: number[] = [];

    const documentsIndex = TABS_DETAIL.indexOf('Avis et documents');
    const faqIndex = TABS_DETAIL.indexOf('Questions / Réponses');
    if (documentsIndex !== -1) inactiveTabs.push(documentsIndex);
    if (faqIndex !== -1) inactiveTabs.push(faqIndex);

    return inactiveTabs;
  }, [candidature, TABS_DETAIL]);

  const handleTabChange = useCallback((tabIndex: number) => {
    const disabledTabs = getDisabledTabs();
    const inactiveTabs = getInactiveTabs();
    if (!disabledTabs.includes(tabIndex) || !inactiveTabs.includes(tabIndex)) {
      setActiveTab(tabIndex);
    }
  }, [getDisabledTabs, getInactiveTabs]);

  if (loading) {
    return (
      <div className="detail-loading">
        <div className="spinner" />
        <p>Chargement de la candidature…</p>
      </div>
    );
  }

  if (!candidature) {
    return (
      <div className="detail-error">
        <p>Candidature introuvable.</p>
        <Button
          type="button"
          variant="secondary"
          value="Retour"
          size="small"
          onClick={() => navigate('/publications?tab=mes-candidatures')}
        />
      </div>
    );
  }

  const estMandataire = candidature.is_mandataire === true;
  const isRegional = lotCodeIsRegional(candidature.lot_technique.code);

  // Groupes de régions pour lots 1xx (lots_departements actuellement sélectionnés)
  const regionGroups: RegionGroup[] = isRegional
    ? buildRegionGroups(candidature.lots_departements, lotsGeoIds)
    : [];

  // ── Validation pré-soumission ────────────────────────────────────────────────

  const lotsOk = lotsGeoIds.length > 0;

  const offreTechniqueOk = CRITERES_TECHNIQUES.every(
    (c) => (offreTechnique[c.slug] ?? '').trim().length > 0,
  );

  const offreFinanciereOk = (() => {
    const articles = candidature.lot_technique.articles ?? [];
    if (articles.length === 0) return true;
    if (prixIdentiques) {
      return articles.every((a) => {
        const p = prixLignes[a.id];
        return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
      });
    }
    if (isRegional) {
      // Lot 1xx : vérifier que chaque région a tous ses articles renseignés
      return regionGroups.length > 0 && regionGroups.every(({ regionId }) => {
        const regPrix = prixParRegion[regionId];
        return articles.every((a) => {
          const p = regPrix?.[a.id];
          return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
        });
      });
    }
    return lotsGeoIds.length > 0 && lotsGeoIds.every((lotId) => {
      const lotPrix = prixParLot[lotId];
      return articles.every((a) => {
        const p = lotPrix?.[a.id];
        return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
      });
    });
  })();

  const validationItems: ValidationItem[] = [
    {
      label: 'Lots géographiques',
      ok: lotsOk,
      detail: lotsOk ? `${lotsGeoIds.length} lot(s) sélectionné(s)` : 'Sélectionnez au moins un lot géographique',
    },
    {
      label: 'Offre technique',
      ok: offreTechniqueOk,
      detail: offreTechniqueOk ? 'Les 4 critères sont renseignés' : 'Renseignez les 4 critères techniques',
    },
    {
      label: 'Offre financière',
      ok: offreFinanciereOk,
      detail: offreFinanciereOk ? 'Tous les prix sont renseignés' : 'Renseignez tous les prix',
    },
    ...(checkProfils
      ? checkProfils.profils.map((p) => ({
        label: `${p.entreprise_nom} (${p.mandataire ? 'mandataire' : 'cotraitant'})`,
        ok: p.progression === 100,
        detail: p.progression === 100 ? 'Profil complet' : `Profil à ${p.progression}% — doit être à 100% avant dépôt`,
      }))
      : [{ label: 'Profils membres', ok: null as null, detail: 'Vérification en cours…' }]
    ),
  ];

  // ── Handlers ────────────────────────────────────────────────────────────────

  /**
   * Affiche le contenu de l'onglet actif
   */
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="tab-content">
            <div>
              <p>Contenu de l'onglet "Avis et documents"</p>
            </div>
          </div>
        );
      case 1:
        return (
          <>
            {/* Section 1 — Candidat / Groupement */}
            <SectionCandidat
              entite={candidature.entite}
              groupementMembers={candidature.groupement_members ?? []}
              estMandataire={estMandataire}
              isSoumise={candidature.status === 'soumise'}
              onRetirer={handleRetirerMembre}
              onInviter={() => setShowInviterModal(true)}
              onViewMemberProfile={openMemberProfileModal}
            />

            {/* Section 2 — Choix des lots */}
            <SectionChoixLots
              lotTechnique={candidature.lot_technique}
              lotsDisponibles={candidature.lots_disponibles ?? []}
              selectedIds={lotsGeoIds}
              canEdit={candidature.status !== 'soumise'}
              onLotsGeoChange={handleLotsGeoChange}
              autoSaveStatus={lotsAutoSave.status}
              autoSaveError={lotsAutoSave.error}
            />

            {/* Section 3 — Offre technique */}
            <SectionOffreTechnique
              poidsOffreTechnique={candidature.accord_cadre.poids_offre_technique ?? 45}
              canEdit={candidature.status !== 'soumise'}
              offreTechnique={offreTechnique}
              onChangeCritere={handleChangeCritere}
              autoSaveStatus={otAutoSave.status}
              autoSaveError={otAutoSave.error}
            />

            {/* Section 4 — Offre financière */}
            <SectionOffreFinanciere
              poidsOffreFinanciere={candidature.accord_cadre.poids_offre_financiere ?? 55}
              articles={candidature.lot_technique.articles ?? []}
              canEdit={candidature.status !== 'soumise'}
              isRegional={isRegional}
              regionGroups={regionGroups}
              prixIdentiques={prixIdentiques}
              prixLignes={prixLignes}
              prixParLot={prixParLot}
              prixParRegion={prixParRegion}
              selectedLots={candidature.lots_departements.filter((l) => lotsGeoIds.includes(l.id))}
              onTogglePrixIdentiques={() => {
                if (prixIdentiques) {
                  // identiques → différents : PAS de popup, pré-remplir les accordéons
                  setPrixIdentiques(false);
                  if (isRegional) {
                    const newPrixParRegion: Record<number, Record<number, PrixLigne>> = {};
                    regionGroups.forEach(({ regionId }) => {
                      newPrixParRegion[regionId] = { ...prixLignes };
                    });
                    setPrixParRegion(newPrixParRegion);
                  } else {
                    const newPrixParLot: Record<number, Record<number, PrixLigne>> = {};
                    lotsGeoIds.forEach((lotId) => {
                      newPrixParLot[lotId] = { ...prixLignes };
                    });
                    setPrixParLot(newPrixParLot);
                  }
                } else {
                  // différents → identiques : POPUP de confirmation
                  setShowTogglePrixConfirm(true);
                }
              }}
              onChangePrix={handleChangePrix}
              onChangePrixLot={handleChangePrixLot}
              onChangePrixRegion={handleChangePrixRegion}
              autoSaveStatus={ofAutoSave.status}
              autoSaveError={ofAutoSave.error}
            />

            {/* Section 5 — Révision + Soumission (Story 8.9) */}
            <SectionSoumission
              isSoumise={candidature.status === 'soumise'}
              estMandataire={estMandataire}
              validationItems={validationItems}
              isDeposing={isDeposing}
              acceptConditions={acceptConditions}
              onToggleAccept={() => setAcceptConditions((v) => !v)}
              onDeposer={handleDeposer}
            />
          </>
        );
      case 2:
        return (
          <div className="tab-content">
            Questions / Réponses
          </div>
        );
      default:
        return null;
    }
  };

  const handleLotsGeoChange = (ids: number[]) => {
    setLotsGeoIds(ids);
    setCandidature((prev) => prev ? { ...prev, lots_geo_ids: ids } : prev);
  };

  const handleRetirerMembre = (entrepriseId: number) => {
    if (!window.confirm('Retirer ce membre du groupement ?')) return;
    fetch(
      `${apiUrl}/publications/ac27/candidatures/${id}/membres/${entrepriseId}`,
      { method: 'DELETE', headers: authHeaders },
    ).then(() => loadCandidature());
  };

  const handleChangeCritere = (slug: string, value: string) => {
    setOffreTechnique((prev) => ({ ...prev, [slug]: value }));
  };

  const handleChangePrix = (
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    setPrixLignes((prev) => ({
      ...prev,
      [articleId]: { ...(prev[articleId] ?? PRIX_LIGNE_VIDE), [champ]: val },
    }));
  };

  const handleChangePrixRegion = (
    regionId: number,
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    setPrixParRegion((prev) => ({
      ...prev,
      [regionId]: {
        ...(prev[regionId] ?? {}),
        [articleId]: {
          ...(prev[regionId]?.[articleId] ?? PRIX_LIGNE_VIDE),
          [champ]: val,
        },
      },
    }));
  };

  const handleChangePrixLot = (
    lotId: number,
    articleId: number,
    champ: keyof PrixLigne,
    val: string,
  ) => {
    setPrixParLot((prev) => ({
      ...prev,
      [lotId]: {
        ...(prev[lotId] ?? {}),
        [articleId]: {
          ...(prev[lotId]?.[articleId] ?? PRIX_LIGNE_VIDE),
          [champ]: val,
        },
      },
    }));
  };

  // Ouvre la popup de confirmation
  const handleDeposer = () => {
    setDeposerError(null);
    setShowConfirmDeposer(true);
  };

  // Appelé depuis la popup après confirmation
  const handleConfirmDeposer = () => {
    setIsDeposing(true);
    setDeposerError(null);
    fetch(`${apiUrl}/publications/ac27/candidatures/${id}/deposer`, {
      method: 'POST',
      headers: authHeaders,
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.message ?? `Erreur ${r.status}`);
        }
        setShowConfirmDeposer(false);
        navigate('/publications?tab=mes-candidatures');
      })
      .catch((e: Error) => setDeposerError(e.message))
      .finally(() => setIsDeposing(false));
  };

  // ── Rendu ────────────────────────────────────────────────────────────────────



  return (
    <div className="candidature-detail-container">
      {/* <CandidatureHeader
        accordCadreNom={candidature.accord_cadre.nom}
        acheteur={candidature.accord_cadre.acheteur}
        lotTechniqueNom={candidature.lot_technique.nom}
        lotTechniqueCpv={candidature.lot_technique.cpv}
        referenceAvis={candidature.accord_cadre.reference_avis}
        status={candidature.status}
        delais={candidature.accord_cadre.expire_at}
        onRetour={() => navigate('/publications?tab=mes-candidatures')}
      /> */}
      <div className='no-print'>
        <SuiviHeader
          titre={'ACCORD CADRE édition 2027-2028'}
          sousTitre={candidature.accord_cadre.nom}
          subtitle={`${candidature.lot_technique?.id} - ${candidature.lot_technique?.nom}, ${candidature.lots_departements?.[0]?.id} - ${candidature.lots_departements?.[0]?.departement?.nom}`}
          status={candidature.status || 'Statut inconnu'} // Assurer une valeur par défaut
          statusColor={getStatusColor(candidature.status)}
          delais={candidature.accord_cadre.expire_at}
          backgroundImage={getLotImage(candidature.lot_technique?.nom)}
          source="MS"
          onRetour={() => navigate('/publications?tab=mes-candidatures')}
        />
      </div>

      {/* <TabNav
        tabs={TABS_DETAIL}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        inactiveTabs={[0, 2]}
        inactiveTabsTooltips={{
          'Avis et documents': 'Fonctionnalité en cours de développement',
          'Questions / Réponses': 'Fonctionnalité en cours de développement',
        }}
      /> */}

      <div className="suivi-de-commande-content">
        <TabNav
          tabs={TABS_DETAIL}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disabledTabs={getDisabledTabs()}
          inactiveTabs={getInactiveTabs()}
        />

        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Modal inviter un membre (Story 8.5 — placeholder) */}
      {showInviterModal && (
        <div className="modal-backdrop" onClick={() => setShowInviterModal(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Inviter un membre au groupement</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowInviterModal(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="tab-placeholder">
                Formulaire d'invitation (Story 8.5 — à implémenter)
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Popup confirmation dépôt définitif */}
      <ValidationPopUp
        isOpen={showConfirmDeposer}
        title="Confirmer le dépôt"
        text="Cette action est irréversible. La candidature sera soumise définitivement et ne pourra plus être modifiée."
        confirmLabel="Soumettre définitivement"
        cancelLabel="Annuler"
        isLoading={isDeposing}
        onConfirm={handleConfirmDeposer}
        onClose={() => { setShowConfirmDeposer(false); setDeposerError(null); }}
      >
        {deposerError && (
          <p style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.375rem', padding: '0.6rem 1rem', margin: '0.5rem 0 0', fontSize: '1.2rem' }}>
            ✗ {deposerError}
          </p>
        )}
      </ValidationPopUp>

      {/* Popup confirmation toggle prix identiques/différents (Story 13.5) */}
      <ValidationPopUp
        isOpen={showTogglePrixConfirm}
        title="Changer le mode de prix"
        text={isRegional
          ? `Attention : les prix de la première région (${regionGroups[0]?.regionNom ?? 'première région'}) seront appliqués à toutes les autres régions. Les prix différents que vous avez saisis seront écrasés.`
          : `Attention : les prix du premier département seront appliqués à tous les autres départements. Les prix différents que vous avez saisis seront écrasés.`
        }
        confirmLabel="Confirmer le changement"
        cancelLabel="Annuler"
        onConfirm={() => {
          setPrixIdentiques(true);
          if (isRegional) {
            const firstGroup = regionGroups[0];
            if (firstGroup && prixParRegion[firstGroup.regionId]) {
              setPrixLignes({ ...prixParRegion[firstGroup.regionId] });
            }
          } else {
            const firstLotId = lotsGeoIds[0];
            if (firstLotId && prixParLot[firstLotId]) {
              setPrixLignes({ ...prixParLot[firstLotId] });
            }
          }
          setShowTogglePrixConfirm(false);
        }}
        onClose={() => setShowTogglePrixConfirm(false)}
      />

      {/* Barre d'actions fixe en bas (Story 13.7) */}
      {candidature.status !== 'soumise' && (
        <BottomActionBar
          onExportPdf={() => { /* TODO: export PDF */ }}
          onQuitter={handleQuitter}
          onDeposer={handleDeposer}
          canDeposer={estMandataire && acceptConditions && validationItems.every((item) => item.ok === true) && !isDeposing}
        />
      )}

      {/* Story 8.12 — Modale profil membre groupement (lecture seule) */}
      <ModalProfilMembre
        isOpen={showMemberProfileModal}
        onClose={() => { setShowMemberProfileModal(false); setMemberProfile(null); setMemberProfileError(null); }}
        profile={memberProfile}
        isLoading={memberProfileLoading}
        error={memberProfileError}
      />
    </div>
  );
};

export default DetailCandidature;
